import type { ComponentProps, ReactNode } from 'react';
import { FieldInfo, hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { Input } from '../input';
import { cn } from '../../lib/utils';
import { ExportIcon } from '../../custom/export-icon';
import { FieldDescription, FieldLabelRow } from './field-label-row';
import { CharacterCount, type RecommendedLength } from './character-count';

export default function TextField({
	label,
	className,
	orientation,
	description,
	optional,
	info,
	badge,
	recommendedLength,
	readOnlyAsLink,
	fieldSize,
	...props
}: ComponentProps<'input'> & {
	label?: string;
	orientation?: 'vertical' | 'horizontal';
	variant?: 'default' | 'outline' | 'background';
	description?: ReactNode;
	optional?: boolean;
	info?: ReactNode;
	badge?: ReactNode;
	recommendedLength?: RecommendedLength;
	readOnlyAsLink?: boolean | ((value: string) => string);
	fieldSize?: 'default' | 'lg';
}) {
	const field = useFieldContext<string>();
	const formReadOnly = useFormReadOnly();
	const isReadOnly = props.readOnly ?? formReadOnly;
	const isOptional = useIsFieldOptional(field.name, optional);
	const isHorizontal = orientation === 'horizontal';
	const showAsLink = isReadOnly && readOnlyAsLink && !!field.state.value;
	const linkHref = showAsLink
		? typeof readOnlyAsLink === 'function'
			? readOnlyAsLink(field.state.value)
			: field.state.value
		: undefined;

	return (
		<div className={className}>
			<div className={isHorizontal ? 'flex items-start gap-3' : 'grid gap-2'}>
				{label && (
					<FieldLabelRow
						label={label}
						htmlFor={field.name}
						hasError={field.state.meta.isTouched && !field.state.meta.isValid}
						optional={isOptional}
						info={info}
						badge={badge}
					/>
				)}
				{description && <FieldDescription>{description}</FieldDescription>}
				<div className={isHorizontal ? 'flex-1' : undefined}>
					{readOnlyAsLink ? (
						<div
							className={cn(
								'flex h-8 items-center rounded-lg border bg-transparent text-base transition-[color,border-color,padding,box-shadow] md:text-sm',
								isReadOnly ? 'border-transparent px-0' : 'border-input focus-within:border-ring px-2.5',
							)}
						>
							{showAsLink ? (
								<a
									href={linkHref}
									target="_blank"
									rel="noopener noreferrer"
									className="text-foreground inline-flex items-center gap-1.5 underline underline-offset-2"
								>
									<span>{field.state.value}</span>
									<ExportIcon className="text-foreground size-4" />
								</a>
							) : (
								<input
									id={field.name}
									name={field.name}
									aria-invalid={hasFieldError(field.state.meta)}
									value={isReadOnly && !field.state.value ? '-' : (field.state.value ?? '')}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									{...props}
									readOnly={isReadOnly}
									tabIndex={isReadOnly ? -1 : undefined}
									className="min-w-0 flex-1 bg-transparent outline-none"
									placeholder={props.placeholder}
								/>
							)}
						</div>
					) : (
						<Input
							id={field.name}
							name={field.name}
							aria-invalid={hasFieldError(field.state.meta)}
							value={isReadOnly && !field.state.value ? '-' : (field.state.value ?? '')}
							onBlur={field.handleBlur}
							onChange={(e) => field.handleChange(e.target.value)}
							{...props}
							readOnly={isReadOnly}
							tabIndex={isReadOnly ? -1 : undefined}
							className={cn(
								fieldSize === 'lg' && 'h-12',
								isReadOnly && 'border-transparent px-0 focus-visible:ring-0 focus-visible:border-transparent',
							)}
						/>
					)}
					{recommendedLength && <CharacterCount value={field.state.value ?? ''} recommended={recommendedLength} />}
					<FieldInfo fieldState={field.state} />
				</div>
			</div>
		</div>
	);
}
